import Icon from 'components/_UI/Icon';
import Text from 'components/_UI/Text';
import date from 'helpers/date';
import useFavorite from 'hooks/useFavorite';
import React from 'react';
import { Calendar, Heart, MapPin } from 'lucide-react';
import styled from 'styled-components';
import { FullyCentered } from 'styled/alignment/Center';
import { Column } from 'styled/alignment/Column';
import { Row } from 'styled/alignment/Row';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import userStatus from 'theme/userStatus';
import { useHighlightedEventOptional } from 'context/events/HighlightedEvent';

export default function EventCardV2({
    data,
    withoutTags = false,
    onEventClick,
    expired = false,
}) {
    const { isFavorited, toggleFavorite } = useFavorite(data?.slug, data?.user_status === userStatus.FAVORITE, data);
    const { highlightedSlug, highlightedSource, highlightEvent, clearHighlight } = useHighlightedEventOptional();

    const cardRef = React.useRef(null);
    const isHighlighted = !!data?.slug && highlightedSlug === data.slug;

    // Quando o destaque nasce no mapa, traz o card correspondente para a vista.
    // Sem isso o pin acende mas o card pode estar fora da área visível da lista.
    React.useEffect(() => {
        if (isHighlighted && highlightedSource === 'map') {
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isHighlighted, highlightedSource]);

    const eventDate = data?.dates?.[0]?.date ? date.format(data.dates[0].date) : 'Data a definir';
    const eventTime = data?.dates?.[0]?.start ? date.formatTime(data.dates[0].start) : '--:--';

    const addressText = data?.address?.street
        ? `${data.address.street.name}, Bairro ${data.address.street.district}`
        : data?.address?.raw_address || null;

    const eventHref = data?.slug ? `/evento/${data.slug}` : undefined;

    // O card abre o detalhe em modal, mas continua sendo um link de verdade: assim
    // ele recebe foco, abre por Enter e responde a Ctrl/Cmd/clique-do-meio abrindo
    // em nova aba — comportamento esperado num catálogo, onde se compara eventos
    // lado a lado. Só interceptamos o clique simples, sem modificadores.
    function handleClick(e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
            return;
        }

        e.preventDefault();
        onEventClick(data);
    }

    return (
        <CardContainer
            id='event_card'
            ref={cardRef}
            as={eventHref ? 'a' : 'div'}
            href={eventHref}
            onClick={handleClick}
            expired={expired}
            highlighted={isHighlighted}
            onMouseEnter={() => highlightEvent(data?.slug, 'list')}
            onMouseLeave={clearHighlight}
            onFocus={() => highlightEvent(data?.slug, 'list')}
            onBlur={clearHighlight}
        >
            <ImageWrapper>
                <CardImage
                    src={data?.images?.length && data?.images[0] || data.path || '/assets/images/placeholder-events.png'}
                    alt={data?.name ? `Imagem do evento ${data.name}` : 'Imagem do evento'}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                />

                <TopTypesContainer>
                    {(() => {
                        const isFree = data?.classification?.name === eventTypes.free.slug;
                        return (
                            <Tag isType={isFree ? 'free' : 'paid'}>
                                <Text name='mini' color={colors.neutralLight} paddingBottom={0}>
                                    {isFree ? 'Gratuito' : 'Pago'}
                                </Text>
                            </Tag>
                        );
                    })()}

                    {data?.trending && (
                        <Tag isTrend>
                            <Column width='16px' height='16px'>
                                <Icon name='fire-filled' color={colors.occasionalPink} />
                            </Column>
                            <Text
                                name='mini'
                                color={colors.occasionalPink}
                                paddingBottom={0}
                                marginLeft={'4px'}
                            >
                                Em alta
                            </Text>
                        </Tag>
                    )}
                </TopTypesContainer>

                <FavoriteContent
                    as='button'
                    type='button'
                    aria-pressed={isFavorited}
                    aria-label={isFavorited
                        ? `Remover ${data?.name || 'evento'} dos favoritos`
                        : `Favoritar ${data?.name || 'evento'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFavorite();
                    }}
                >
                    <Heart
                        size={14}
                        color={isFavorited ? colors.occasionalPink : colors.night}
                        fill={isFavorited ? colors.occasionalPink : 'none'}
                    />
                </FavoriteContent>
            </ImageWrapper>

            <CardContent>
                <Text type='bold' color={colors.nightDark} ellipsisAt='95%' paddingBottom={0}>
                    {data.name}
                </Text>

                {/* colors.night (#707070) em vez de nightLight (#ACACAC): data e local
                    são os dados que decidem a ida ao evento e estavam a 2,27:1, abaixo
                    dos 4,5:1 exigidos pela WCAG AA. Este tom dá 4,9:1. */}
                <Row verticalCenter>
                    <Calendar size={13} color={colors.night} />
                    <Text name='small' color={colors.night} paddingBottom={0} marginLeft='4px'>
                        {eventDate} · {eventTime}
                    </Text>
                </Row>

                <Row verticalCenter>
                    <MapPin size={13} color={colors.night} />
                    <Text name='small' color={colors.night} ellipsisAt='95%' paddingBottom={0} marginLeft='4px'>
                        {addressText || 'Local a definir'}
                    </Text>
                </Row>

                {!withoutTags && data?.tags?.length > 0 && (
                    <TagsContainer>
                        <Text name='mini' ellipsisAt='95%' paddingBottom='0px' marginLeft='3px'>
                            {data?.tags?.map(item => `#${item.name} `)}
                        </Text>
                    </TagsContainer>
                )}
            </CardContent>
        </CardContainer>
    )
};

const CardContainer = styled(Column)`
    width: 100%;
    cursor: pointer;
    position: relative;
    background: transparent;
    text-decoration: none;
    color: inherit;

    ${({ expired }) => expired && `opacity: 0.6;`}

    &:hover img {
        transform: translateY(-2px);
        box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
    }

    /* Destaque recebido do mapa: o hover não serve aqui porque o cursor está
       sobre o pin, do outro lado da tela. */
    ${({ highlighted }) => highlighted && `
        & img {
            transform: translateY(-2px);
            box-shadow: 0 0 0 2px ${colors.occasionalPurple}, 0 4px 18px rgba(159, 22, 219, 0.22);
        }
    `}

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 3px;
        border-radius: 12px;
    }
`;

const ImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 120px;
    aspect-ratio: 3 / 2;
    border-radius: 12px;
    overflow: hidden;
`;

const CardImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    border-radius: 12px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

const TopTypesContainer = styled(Row)`
    position: absolute;
    top: 10px;
    left: 8px;
    width: auto;
`;

const Tag = styled(FullyCentered)`
    flex-direction: row;
    padding: 4px 8px;
    border-radius: 6px;
    margin-right: 4px;
    width: auto;

    &:last-child {
        margin-right: 0;
    }

    background: ${({ isType, isTrend }) =>
        isTrend ? 'rgba(255, 255, 255, 0.92)' :
            isType === 'free' ? 'rgba(43, 200, 102, 0.85)' :
                isType === 'paid' ? 'rgba(159, 22, 219, 0.85)' :
                    'rgba(255, 255, 255, 0.92)'
    };
    border: none;

    ${({ isTrend }) => isTrend && `
        border: 1px solid ${colors.occasionalPink}40;
    `}
`;

const FavoriteContent = styled(FullyCentered)`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    border: none;
    padding: 0;

    &:focus-visible {
        outline: 2px solid ${colors.occasionalPurple};
        outline-offset: 2px;
    }
`;

const CardContent = styled(Column)`
    padding: 8px 4px 4px;
    gap: 4px;
`;

const TagsContainer = styled(Row)`
    align-items: center;
    margin-top: 4px;
`;
